import { firstPage } from './pdfPage';

/**
 * A ticket's QR code read off its page or its photo with jsQR, in the browser: nothing is sent.
 * jsQR loads only once a file is looked at.
 */

/** The text of the QR code drawn on a canvas; `null` when it has none, or cannot be read. */
export async function codeOn(canvas: HTMLCanvasElement): Promise<string | null> {
  const drawn = canvas
    .getContext('2d', { willReadFrequently: true })
    ?.getImageData(0, 0, canvas.width, canvas.height);
  if (drawn === undefined) return null;
  const { default: jsQR } = await import('jsqr');
  return jsQR(drawn.data, drawn.width, drawn.height)?.data ?? null;
}

/** The text of the QR code in a picture, drawn on a canvas of its own size to be read. */
export function codeIn(picture: HTMLImageElement): Promise<string | null> {
  const canvas = document.createElement('canvas');
  canvas.width = picture.naturalWidth;
  canvas.height = picture.naturalHeight;
  canvas.getContext('2d')?.drawImage(picture, 0, 0);
  return codeOn(canvas);
}

/** A picture drawn from its address once it has loaded; never, for one that cannot be. */
function loaded(url: string): Promise<HTMLImageElement> {
  return new Promise((done, failed) => {
    const picture = new Image();
    picture.onload = () => {
      done(picture);
    };
    picture.onerror = failed;
    picture.src = url;
  });
}

/**
 * The QR code of a file kept with a booking — a PDF's first page, or a photo — read whether or
 * not it is the one shown: a boarding pass beside its invoice; `null` when it has none.
 */
export async function codeOfFile(file: File): Promise<string | null> {
  const url = URL.createObjectURL(file);
  try {
    const page = file.type === 'application/pdf' ? await firstPage(url) : file;
    const drawn = URL.createObjectURL(page);
    try {
      return await codeIn(await loaded(drawn));
    } finally {
      URL.revokeObjectURL(drawn);
    }
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}
